import React, { useState } from 'react';
import styled from 'styled-components';
import { Space, Tag, Popconfirm, Select, Button, Tooltip, message } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import VolunteerRegistrationService from '../../../services/VolunteerRegistrationService';
import {
  StyledTable,
  StyledTableContainer,
  PaginationText
} from '../styled';

const SmallButton = styled(Button)`
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  font-size: 14px !important;
  padding: 0 15px !important;
`;

const RegistrationsTable = ({
  registrations,
  registrationsLoading,
  registrationStatusFilter,
  onRegistrationStatusFilterChange,
  registrationsPagination,
  onFetchRegistrations,
  onUpdateRegistrationStatus,
  onViewRegistrationWaiver
}) => {
  const columns = [
    {
      title: 'Name',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (text, record) => (
        <span style={{ fontWeight: 500 }}>{text || `${record.firstName} ${record.lastName}`}</span>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (text) => text || '-'
    },
    {
      title: 'Volunteer Area',
      dataIndex: 'volunteerArea',
      key: 'volunteerArea',
      render: (text) => text || '-'
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => {
        if (record.preferredCity && record.preferredState) {
          return `${record.preferredCity}, ${record.preferredState}`;
        } else if (record.preferredCity) {
          return record.preferredCity;
        }
        return record.isGeneralVolunteer ? 'Any Location' : '-';
      }
    },
    {
      title: 'Event',
      key: 'selectedEvent',
      render: (_, record) => {
        if (!record.selectedEventName && !record.selectedEventId) {
          return '-';
        }
        const name = record.selectedEventName || 'Selected Event';
        const date = record.selectedEventDate
          ? new Date(record.selectedEventDate).toLocaleDateString()
          : null;
        return (
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontWeight: 500 }}>{name}</div>
            {date && <div style={{ fontSize: 12, color: '#8c8c8c' }}>{date}</div>}
          </div>
        );
      }
    },
    {
      title: 'Waiver',
      key: 'waiver',
      render: (_, record) => (
        record.waiverSignedAt || record.signatureData ? (
          <Button 
            type="link" 
            size="small" 
            style={{ padding: 0, color: '#52c41a' }}
            onClick={() => onViewRegistrationWaiver(record)}
          >
            View Waiver
          </Button>
        ) : (
          <Tag color="orange">Not Signed</Tag>
        )
      )
    },
    {
      title: 'Submitted',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space size={4}>
          {record.status === 'pending' && (
            <>
              <Popconfirm
                title="Approve this registration?"
                description="The volunteer will be notified via email."
                onConfirm={() => onUpdateRegistrationStatus(record._id, 'approved')}
                okText="Approve"
                cancelText="Cancel"
              >
                <button 
                  type="button"
                  style={{ 
                    backgroundColor: '#1890ff', 
                    borderColor: '#1890ff', 
                    border: '1px solid #1890ff',
                    color: 'white',
                    fontSize: '11px', 
                    padding: '2px 8px', 
                    height: '22px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Approve
                </button>
              </Popconfirm>
              <Popconfirm
                title="Reject this registration?"
                description="The volunteer will be notified via email."
                onConfirm={() => onUpdateRegistrationStatus(record._id, 'rejected')}
                okText="Reject"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <button 
                  type="button"
                  style={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #ff4d4f',
                    color: '#ff4d4f',
                    fontSize: '11px', 
                    padding: '2px 8px', 
                    height: '22px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Reject
                </button>
              </Popconfirm>
            </>
          )}
          {record.status !== 'pending' && (
            <Tag color={record.status === 'approved' ? 'green' : 'red'}>
              {record.status === 'approved' ? 'Approved' : 'Rejected'}
            </Tag>
          )}
        </Space>
      )
    }
  ];

  const [syncing, setSyncing] = useState(false);

  const handleSyncVolunteers = async () => {
    try {
      setSyncing(true);
      const response = await VolunteerRegistrationService.syncApprovedToUsers();
      if (response.success) {
        const { created, upgraded, skipped } = response.data || {};
        message.success(response.message || `Sync complete: ${created} created, ${upgraded} upgraded, ${skipped} already synced`);
      } else {
        message.error(response.message || 'Failed to sync volunteers');
      }
    } catch (error) {
      console.error('Error syncing volunteers:', error);
      message.error('Failed to sync volunteers');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="table-animate-in">
      {/* Status Filter for Registrations */}
      <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ color: '#8c8c8c' }}>Status:</span>
          <Select
            value={registrationStatusFilter}
            onChange={onRegistrationStatusFilterChange}
            style={{ width: 150 }}
          >
            <Select.Option value="all">All Status</Select.Option>
            <Select.Option value="pending">Pending</Select.Option>
            <Select.Option value="approved">Approved</Select.Option>
            <Select.Option value="rejected">Rejected</Select.Option>
          </Select>
        </div>
        <Tooltip title="Create user accounts for all approved registrations that don't have one yet">
          <SmallButton
            icon={<SyncOutlined spin={syncing} />}
            onClick={handleSyncVolunteers}
            loading={syncing}
          >
            Sync to Users
          </SmallButton>
        </Tooltip>
      </div>
      
      <StyledTableContainer>
        <StyledTable
          className="user-management-pagination"
          columns={columns}
          dataSource={registrations}
          rowKey="_id"
          loading={registrationsLoading}
          size="middle"
          locale={{
            emptyText: registrationsLoading ? 'Loading registrations...' : 'No registrations found'
          }}
          scroll={{ x: 900 }}
          pagination={{
            ...registrationsPagination,
            onChange: (page, pageSize) => onFetchRegistrations(page, pageSize),
            onShowSizeChange: (_, pageSize) => onFetchRegistrations(1, pageSize),
            showTotal: (total, range) => (
              <PaginationText>
                Showing {range[0]}-{range[1]} of {total} registrations
              </PaginationText>
            ),
            className: 'user-management-pagination'
          }}
        />
      </StyledTableContainer>
    </div>
  );
};

export default RegistrationsTable;
